// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useState } from "react";
import { Table, Loader, Segment } from "semantic-ui-react";
import * as valuePrecisionConverter from "../../../Lumber/ValuePrecisionConverter";

import _ from "lodash";

import type { AnovaTableProps } from "./types";

/** A display cell: "-", "<0.00001", a fixed-decimal string, or a precision-converted number. */
type NormalizedValue = string | number;

const AnovaTable: FC<AnovaTableProps> = ({
	anovaData,
	compiledDataLoading,
	hideData = true,
	wasThereAnError = false,
}) => {
	//-- Data
	// TODO(types): normalizeData returns undefined when an anovaData column is null, after which the
	// render's `xs[0]` throws. The `!` below asserts it away.
	const [denominatorDfs, setDenominatorDfs] = useState<NormalizedValue[] | undefined>([]);
	const [fValues, setFValues] = useState<NormalizedValue[] | undefined>([]);
	const [pValues, setPValues] = useState<NormalizedValue[] | undefined>([]);

	//-- UI Control
	// TODO(types): compiledDataLoading is never passed, so the mount effect sets this to undefined,
	// which is what lets the table render before anovaData arrives.
	const [tableLoading, setTableLoading] = useState<boolean | undefined>(true);

	useEffect(() => {
		if (anovaData) {
			setupTableData();
		}
	}, [anovaData]);

	useEffect(() => {
		setTableLoading(compiledDataLoading);
	}, [compiledDataLoading]);

	const setupTableData = () => {
		setDenominatorDfs(normalizeData(anovaData?.denominatorDf, 1, false));
		setFValues(normalizeData(anovaData?.fValues, 5, true));
		setPValues(normalizeData(anovaData?.pValues, 5, false));

		setTableLoading(false);
	};

	const normalizeData = (
		data: string[] | null | undefined,
		decimalsToRound: number,
		runPrecisionConversion: boolean,
	): NormalizedValue[] | undefined => {
		if (data) {
			return _.map(data, (d) => {
				if (d === "-") {
					return d;
				} else {
					const parsedNumber = parseFloat(d);
					const fixedNumber = parsedNumber.toFixed(decimalsToRound);
					if (runPrecisionConversion === true) {
						// ValuePrecisionConverter is untyped JS; a non-empty string input yields a number.
						return valuePrecisionConverter.getPrecision(fixedNumber) as number;
					}

					return Number(fixedNumber) == 0 ? "<0.00001" : fixedNumber;
				}
			});
		}
		return undefined;
	};

	return tableLoading ? (
		<Loader active />
	) : (
		<Segment basic style={{ padding: 0 }}>
			<Table celled striped selectable>
				<Table.Header>
					<Table.Row textAlign="center">
						<Table.HeaderCell style={{ zIndex: "0" }}></Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>DF</Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>F-Value</Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>p-Value</Table.HeaderCell>
					</Table.Row>
				</Table.Header>

				<Table.Body>
					<Table.Row>
						<Table.HeaderCell textAlign="left">Treatment</Table.HeaderCell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (denominatorDfs![0] ?? "-")}
						</Table.Cell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (fValues![0] ?? "-")}
						</Table.Cell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (pValues![0] ?? "-")}
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.HeaderCell textAlign="left">Trial</Table.HeaderCell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (denominatorDfs![1] ?? "-")}
						</Table.Cell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (fValues![1] ?? "-")}
						</Table.Cell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (pValues![1] ?? "-")}
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.HeaderCell textAlign="left">Treatment:Trial</Table.HeaderCell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (denominatorDfs![2] ?? "-")}
						</Table.Cell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (fValues![2] ?? "-")}
						</Table.Cell>
						<Table.Cell textAlign="left">
							{wasThereAnError ? "N/A" : hideData === true ? "-" : (pValues![2] ?? "-")}
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Segment>
	);
};

export default AnovaTable;
